import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from '../../toasts';
import { getError } from '../../utils';
import AttributionService from '../../services/attribution.service';
import { FormField, Section, Select } from '../EntityList';
import { asId, canManageAttribution, unitOptionLabel } from './attributionAccess';

const ID_KEYS = {
	contract: 'contractId',
	credit: 'creditId',
	bill: 'billId',
	billTemplate: 'billTemplateId',
	asset: 'assetId',
};

const DESCRIPTIONS = {
	contract: 'Unidad de negocio a la que se imputan las cuotas de este contrato. Si queda vacía, toma la de la solución.',
	credit: 'Entidad y unidad de negocio a las que se imputan las cuotas de esta pasividad.',
	bill: 'Unidad de negocio a la que se imputa este gasto. Si queda vacía, toma la de la solución.',
	billTemplate: 'Unidad de negocio a la que se imputan los gastos generados por esta plantilla.',
	asset: 'Unidad por defecto de todos los contratos y gastos de esta solución.',
};

const FIELD_LABELS = {
	asset: 'Unidad de negocio por defecto',
};

/**
 * "Unidad de negocio" selector (SHOW_ATTRIBUTION; units are stored as centers).
 * Self-contained: loads the units (and own entities when `showEntity`),
 * shows each unit's partner next to it, renders a select and
 * saves each change immediately through PUT /attribution/assign — it never
 * touches the enclosing editor's save flow. Non-admins see the value read-only.
 *
 * `value` / `entityId` accept a populated document or a plain id.
 */
function AttributionPicker({ kind, id, value, entityId, showEntity = false, onSaved, className = '' }) {
	const [centers, setCenters] = useState([]);
	const [entities, setEntities] = useState([]);
	const [center, setCenter] = useState(asId(value));
	const [entity, setEntity] = useState(asId(entityId));
	const [saving, setSaving] = useState(false);
	const canEdit = canManageAttribution();

	useEffect(() => {
		setCenter(asId(value));
	}, [value]);

	useEffect(() => {
		setEntity(asId(entityId));
	}, [entityId]);

	useEffect(() => {
		let active = true;
		AttributionService.listCenters({ all: true })
			.then(({ data }) => {
				if (active) setCenters(data.centers || []);
			})
			.catch(() => {});
		if (showEntity) {
			AttributionService.listEntities()
				.then(({ data }) => {
					if (active) setEntities(data.entities || []);
				})
				.catch(() => {});
		}
		return () => {
			active = false;
		};
	}, [showEntity]);

	// Active units only (plus the one currently assigned, even if inactive),
	// narrowed to the chosen entity when the record carries one.
	const centerOptions = useMemo(
		() =>
			centers
				.filter((c) => c.active || c.id === center)
				.filter((c) => !showEntity || !entity || !c.entity || c.entity.id === entity)
				.map((c) => ({ value: c.id, label: unitOptionLabel(c) })),
		[centers, center, entity, showEntity],
	);

	const entityOptions = useMemo(() => entities.map((e) => ({ value: e._id, label: e.name })), [entities]);

	const save = async (patch, revert) => {
		setSaving(true);
		try {
			const { data } = await AttributionService.assign({ [ID_KEYS[kind]]: id, ...patch });
			// Own wording: the server still answers "Atribución guardada".
			toast.success('centerId' in patch ? 'Unidad de negocio guardada' : 'Entidad guardada');
			if (onSaved) onSaved(data);
		} catch (err) {
			toast.error(getError(err));
			revert();
		} finally {
			setSaving(false);
		}
	};

	const handleCenterChange = (e) => {
		const next = e.target.value;
		const previous = center;
		setCenter(next);
		save({ centerId: next || null }, () => setCenter(previous));
	};

	const handleEntityChange = (e) => {
		const next = e.target.value;
		const previous = entity;
		setEntity(next);
		save({ entityId: next || null }, () => setEntity(previous));
	};

	const currentUnit = centers.find((c) => c.id === center);
	const centerName = currentUnit ? unitOptionLabel(currentUnit) : '';
	const entityName = entities.find((e) => e._id === entity)?.name;
	const fieldId = `attribution-${kind}-${id}`;

	return (
		<Section title='Unidad de negocio' description={DESCRIPTIONS[kind]} className={className}>
			<div className='tw:grid tw:grid-cols-1 tw:gap-4 tw:min-[640px]:grid-cols-2'>
				{showEntity && (
					<FormField id={`${fieldId}-entity`} label='Entidad'>
						{canEdit ? (
							<Select
								id={`${fieldId}-entity`}
								options={entityOptions}
								value={entity}
								onChange={handleEntityChange}
								placeholder='Sin entidad'
								disabled={saving}
							/>
						) : (
							<div className='tw:text-[13.5px] tw:text-ink-0'>{entityName || <span className='tw:text-ink-3'>Sin entidad</span>}</div>
						)}
					</FormField>
				)}
				<FormField id={`${fieldId}-center`} label={FIELD_LABELS[kind] || 'Unidad de negocio'}>
					{canEdit ? (
						<Select
							id={`${fieldId}-center`}
							options={centerOptions}
							value={center}
							onChange={handleCenterChange}
							placeholder='Sin unidad (General)'
							disabled={saving}
						/>
					) : (
						<div className='tw:text-[13.5px] tw:text-ink-0'>{centerName || <span className='tw:text-ink-3'>Sin unidad (General)</span>}</div>
					)}
				</FormField>
			</div>
		</Section>
	);
}

AttributionPicker.propTypes = {
	// 'asset' sets the default unit of everything on the asset (its contracts, their liquidations and its bills).
	kind: PropTypes.oneOf(['contract', 'credit', 'bill', 'billTemplate', 'asset']).isRequired,
	id: PropTypes.string.isRequired,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	// Credits without a contract also pick the own entity they belong to.
	showEntity: PropTypes.bool,
	onSaved: PropTypes.func,
	className: PropTypes.string,
};

export default AttributionPicker;
